import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStoredValue } from "../../../util";
import { useToast } from "../../../context-providers/Toast";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { RawAddressWithIndices, SovGridContext } from "./types";
import {
  buildPivotTable,
  buildCrossTab,
  configFromFields,
  CrossTabResult,
  PivotTableResult,
} from "./pivotUtils";
import {
  GROUPABLE_FIELDS,
  GroupableField,
  isFieldDataLoading,
} from "./pivotColumns";
import { PivotTable } from "./PivotTable";
import { CrossTabTable } from "./CrossTabTable";
import { copyTableImage } from "./copyTableImage";
import styles from "./PivotTable.module.css";
import { v4 as uuid } from "uuid";
import {
  String as RtString,
  Literal,
  Union,
  Null,
  Array as RtArray,
  Object as RtObject,
} from "runtypes";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type ViewMode = "hierarchical" | "crosstab";

type SheetConfig = {
  id: string;
  primaryKey: string;
  secondaryKey: string | null;
  viewMode: ViewMode;
};

type NonEmpty<T> = [T, ...T[]];

const SheetConfigArrayRt = RtArray(
  RtObject({
    id: RtString,
    primaryKey: RtString,
    secondaryKey: Union(RtString, Null),
    viewMode: Union(Literal("hierarchical"), Literal("crosstab")),
  })
);

const DEFAULT_SHEETS: NonEmpty<Omit<SheetConfig, "id">> = [
  { primaryKey: "state", secondaryKey: "county", viewMode: "hierarchical" },
  { primaryKey: "year_built", secondaryKey: null, viewMode: "hierarchical" },
  { primaryKey: "construction", secondaryKey: null, viewMode: "hierarchical" },
  { primaryKey: "sic_code", secondaryKey: null, viewMode: "hierarchical" },
  {
    primaryKey: "flood_zone_fema",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "earthquake_zone",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  { primaryKey: "sprinkler", secondaryKey: null, viewMode: "hierarchical" },
  {
    primaryKey: "rms_dist_to_coast",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "katrisk_fema_flood_zone",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "katrisk_flood_score",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "nearmap_roof_age",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "nearmap_roof_material",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "nearmap_roof_shape",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "cape_roof_covering",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "cape_roof_geometry",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  {
    primaryKey: "cape_wildfire_hazard",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
  { primaryKey: "wind_gate", secondaryKey: null, viewMode: "hierarchical" },
  { primaryKey: "eq_gate", secondaryKey: null, viewMode: "hierarchical" },
  { primaryKey: "limit_AOP", secondaryKey: null, viewMode: "hierarchical" },
  {
    primaryKey: "deductible_AOP",
    secondaryKey: null,
    viewMode: "hierarchical",
  },
];

function makeDefaultSheets(): NonEmpty<SheetConfig> {
  const [first, ...rest] = DEFAULT_SHEETS;
  return [
    { id: uuid(), ...first },
    ...rest.map((sheet) => ({ id: uuid(), ...sheet })),
  ];
}

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = "pivot-panel-";
const ACTIVE_KEY_PREFIX = "pivot-panel-active-";

function parseSheets(raw: string | null): NonEmpty<SheetConfig> {
  if (!raw) return makeDefaultSheets();
  try {
    const [first, ...rest] = SheetConfigArrayRt.check(JSON.parse(raw));
    if (first) return [first, ...rest];
  } catch {
    // Corrupted or schema-mismatched data — fall through to defaults
  }
  return makeDefaultSheets();
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const cx = (...names: (string | false | null | undefined)[]) =>
  names.filter(Boolean).join(" ");

const fieldByKey = (key: string | null) =>
  key ? GROUPABLE_FIELDS.find((f) => f.key === key) : undefined;

const sheetTitle = (sheet: SheetConfig) => {
  const primary = fieldByKey(sheet.primaryKey)?.label ?? "Pivot";
  const secondary = fieldByKey(sheet.secondaryKey)?.label;
  return secondary ? `${primary} › ${secondary}` : primary;
};

const fieldOptionTemplate = (option: GroupableField) => (
  <div className={styles.fieldOption}>
    <span className={styles.fieldOptionGroup}>{option.group}</span>
    <span>{option.label}</span>
  </div>
);

const selectedFieldTemplate = (
  option: GroupableField | null,
  props: DropdownProps
) => {
  if (!option) return <span>{props.placeholder}</span>;
  return <span>{option.label}</span>;
};

// ---------------------------------------------------------------------------
// Sheet results — the pivot (or cross-tab) built for one sheet config
// ---------------------------------------------------------------------------

type SheetResults = {
  primary: GroupableField | undefined;
  secondary: GroupableField | undefined;
  isLoading: boolean;
  pivotResult: PivotTableResult | null;
  crossTabResult: CrossTabResult | null;
};

function useSheetResults(
  config: SheetConfig,
  addresses: RawAddressWithIndices[],
  context: SovGridContext
): SheetResults {
  const { primaryKey, secondaryKey, viewMode } = config;
  const primary = useMemo(() => fieldByKey(primaryKey), [primaryKey]);
  const secondary = useMemo(() => fieldByKey(secondaryKey), [secondaryKey]);

  // If either selected field depends on data that's still streaming, defer
  // building the pivot — partial data produces misleading "N/A (not modeled)"
  // inflation that drains as chunks arrive.
  const isLoading =
    isFieldDataLoading(primary, context) ||
    isFieldDataLoading(secondary, context);

  const showCrossTab = viewMode === "crosstab" && !!secondary;

  const pivotResult = useMemo(() => {
    if (!primary || isLoading || showCrossTab) return null;
    return buildPivotTable(
      addresses,
      configFromFields(primary, secondary),
      context
    );
  }, [addresses, context, primary, secondary, showCrossTab, isLoading]);

  const crossTabResult = useMemo(() => {
    if (!primary || !secondary || !showCrossTab || isLoading) return null;
    return buildCrossTab(addresses, primary, secondary, context);
  }, [addresses, context, primary, secondary, showCrossTab, isLoading]);

  return { primary, secondary, isLoading, pivotResult, crossTabResult };
}

// ---------------------------------------------------------------------------
// PivotSheet — the toolbar and table for one sheet
// ---------------------------------------------------------------------------

type PivotSheetProps = {
  addresses: RawAddressWithIndices[];
  context: SovGridContext;
  config: SheetConfig;
  onChange: (id: string, update: Partial<SheetConfig>) => void;
};

const PivotSheet: React.FC<PivotSheetProps> = ({
  addresses,
  context,
  config,
  onChange,
}) => {
  const { id, primaryKey, secondaryKey, viewMode } = config;
  const { primary, secondary, isLoading, pivotResult, crossTabResult } =
    useSheetResults(config, addresses, context);

  const secondaryOptions = useMemo(
    () => GROUPABLE_FIELDS.filter((f) => f.key !== primaryKey),
    [primaryKey]
  );

  const toast = useToast();
  const tableRef = useRef<HTMLTableElement>(null);
  const [isCopying, setIsCopying] = useState(false);

  const copyImage = useCallback(async () => {
    const table = tableRef.current;
    if (table === null) return;
    setIsCopying(true);
    try {
      await copyTableImage(table);
      toast.show({
        severity: "success",
        summary: "Pivot Copied",
        detail: "Paste the image into an email or document.",
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Error Copying Pivot",
        detail: "Something failed while copying the pivot as an image.",
      });
    } finally {
      setIsCopying(false);
    }
  }, [toast]);

  // Cmd/Ctrl+C copies the image only when there is no text selection and no
  // focused field. That way a copy of selected cells or a search term works
  // as usual.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
        return;
      if (event.key.toLowerCase() !== "c") return;
      if (window.getSelection()?.toString()) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.matches("input, textarea, select"))
      )
        return;
      if (tableRef.current === null) return;
      event.preventDefault();
      void copyImage();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [copyImage]);

  return (
    <div className={styles.sheet}>
      <div className={styles.toolbar}>
        <div className={styles.control}>
          <span className={styles.controlLabel}>Group by</span>
          <Dropdown
            value={primary ?? null}
            options={GROUPABLE_FIELDS}
            onChange={(e) =>
              onChange(id, { primaryKey: (e.value as GroupableField).key })
            }
            optionLabel="label"
            dataKey="key"
            itemTemplate={fieldOptionTemplate}
            valueTemplate={selectedFieldTemplate}
            placeholder="Select field…"
            filter
            filterPlaceholder="Search fields"
            className={cx(styles.dropdown, styles.dropdownPrimary)}
            panelClassName={styles.dropdownPanel}
          />
        </div>
        <div className={styles.control}>
          <span className={styles.controlLabel}>Then by</span>
          <Dropdown
            value={secondary ?? null}
            options={secondaryOptions}
            onChange={(e) =>
              onChange(id, {
                secondaryKey: e.value ? (e.value as GroupableField).key : null,
              })
            }
            optionLabel="label"
            dataKey="key"
            itemTemplate={fieldOptionTemplate}
            valueTemplate={selectedFieldTemplate}
            placeholder="None"
            showClear
            filter
            filterPlaceholder="Search fields"
            className={cx(styles.dropdown, styles.dropdownSecondary)}
            panelClassName={styles.dropdownPanel}
          />
        </div>
        {secondary && (
          <div className={styles.segmented} role="group" aria-label="View">
            <button
              type="button"
              className={cx(
                styles.segment,
                viewMode === "hierarchical" && styles.segmentActive
              )}
              onClick={() => onChange(id, { viewMode: "hierarchical" })}
            >
              Grouped
            </button>
            <button
              type="button"
              className={cx(
                styles.segment,
                viewMode === "crosstab" && styles.segmentActive
              )}
              onClick={() => onChange(id, { viewMode: "crosstab" })}
            >
              Cross-tab
            </button>
          </div>
        )}
        <button
          type="button"
          className={styles.toolbarAction}
          onClick={copyImage}
          disabled={isCopying || (!pivotResult && !crossTabResult)}
          title="Copy the pivot to the clipboard as an image (Cmd/Ctrl+C)"
        >
          <i className={isCopying ? "pi pi-spin pi-spinner" : "pi pi-copy"} />
          Copy image
        </button>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <ProgressSpinner
            style={{ width: "28px", height: "28px" }}
            strokeWidth="4"
          />
          <span>Loading data…</span>
        </div>
      ) : crossTabResult && primary && secondary ? (
        <CrossTabTable
          key={`${primaryKey}|${secondaryKey}`}
          data={crossTabResult}
          rowLabel={primary.label}
          colLabel={secondary.label}
          tableRef={tableRef}
        />
      ) : pivotResult && primary ? (
        <PivotTable
          key={`${primaryKey}|${secondaryKey}`}
          pivotKey={primary.key}
          pivotData={pivotResult}
          label={primary.label}
          compare={primary.compare}
          tableRef={tableRef}
        />
      ) : (
        <div className={styles.loading}>Select a field to group by.</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PivotPanel — a workbook of sheets with an Excel-style tab strip
// ---------------------------------------------------------------------------

type PivotPanelProps = {
  addresses: RawAddressWithIndices[];
  context: SovGridContext;
  reportId: string;
};

export const PivotPanel: React.FC<PivotPanelProps> = ({
  addresses,
  context,
  reportId,
}) => {
  const { value: rawSheets, set: saveSheets } = useStoredValue(
    `${STORAGE_KEY_PREFIX}${reportId}`
  );
  const { value: storedActiveId, set: saveActiveId } = useStoredValue(
    `${ACTIVE_KEY_PREFIX}${reportId}`
  );
  const sheets = useMemo(() => parseSheets(rawSheets), [rawSheets]);
  const activeIndex = Math.max(
    0,
    sheets.findIndex((s) => s.id === storedActiveId)
  );
  const active = sheets.find((s) => s.id === storedActiveId) ?? sheets[0];
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tabsRef.current
      ?.querySelector(`[data-sheet-id="${active.id}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active.id]);

  const setSheets = (updater: (prev: SheetConfig[]) => SheetConfig[]) => {
    saveSheets(JSON.stringify(updater(sheets)));
  };

  const updateSheet = (id: string, update: Partial<SheetConfig>) => {
    setSheets((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...update } : s))
    );
  };

  const removeSheet = (id: string) => {
    if (sheets.length <= 1) return;
    const index = sheets.findIndex((s) => s.id === id);
    const remaining = sheets.filter((s) => s.id !== id);
    const nextActive = remaining[Math.min(index, remaining.length - 1)];
    if (id === active.id && nextActive) saveActiveId(nextActive.id);
    setSheets(() => remaining);
  };

  const addSheet = () => {
    const sheet: SheetConfig = {
      id: uuid(),
      primaryKey: "state",
      secondaryKey: null,
      viewMode: "hierarchical",
    };
    setSheets((prev) => [
      ...prev.slice(0, activeIndex + 1),
      sheet,
      ...prev.slice(activeIndex + 1),
    ]);
    saveActiveId(sheet.id);
  };

  const resetToDefaults = () => {
    confirmDialog({
      message: "Reset all pivots to defaults?",
      header: "Reset Pivots",
      icon: "pi pi-refresh",
      acceptClassName: "p-button-danger",
      accept: () => {
        const defaults = makeDefaultSheets();
        setSheets(() => defaults);
        saveActiveId(defaults[0].id);
      },
    });
  };

  const onTabKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const step = e.key === "ArrowRight" ? 1 : -1;
      const next = sheets[(activeIndex + step + sheets.length) % sheets.length];
      if (next) saveActiveId(next.id);
    }
  };

  return (
    <div className={styles.panel}>
      <PivotSheet
        key={active.id}
        addresses={addresses}
        context={context}
        config={active}
        onChange={updateSheet}
      />
      <div className={styles.tabStrip}>
        <button
          type="button"
          className={styles.tabAdd}
          onClick={addSheet}
          title="Add pivot"
        >
          <i className="pi pi-plus" />
        </button>
        <div
          className={styles.tabs}
          role="tablist"
          ref={tabsRef}
          onKeyDown={onTabKeyDown}
        >
          {sheets.map((sheet) => {
            const isActive = sheet.id === active.id;
            return (
              <div
                key={sheet.id}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-sheet-id={sheet.id}
                className={cx(styles.tab, isActive && styles.tabActive)}
                onClick={() => saveActiveId(sheet.id)}
              >
                {sheet.viewMode === "crosstab" && sheet.secondaryKey && (
                  <i className={cx("pi pi-table", styles.tabIcon)} />
                )}
                <span className={styles.tabLabel}>{sheetTitle(sheet)}</span>
                {sheets.length > 1 && (
                  <button
                    type="button"
                    className={styles.tabClose}
                    title="Remove pivot"
                    aria-label={`Remove ${sheetTitle(sheet)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSheet(sheet.id);
                    }}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.stripAction}
          onClick={resetToDefaults}
        >
          <i className="pi pi-refresh" />
          Reset
        </button>
      </div>
    </div>
  );
};
